import { RocheUIVideoPlayerEvent } from '@repo/roche-ui';
import { RefObject } from 'react';
import { TrackingService } from '@rocheglobal/component-library-dot-com/src/services/tracking';
import { AdobeTracking } from '../../tracking-services';

export type TrackedPercentage = Record<number, boolean>;

export const rocheVideoPlayerTracking = (
  event: RocheUIVideoPlayerEvent,
  trackedPercentages: RefObject<TrackedPercentage>,
  videoLoop: boolean,
): void => {
  // For now we don't track videoloop's:
  if (videoLoop) {
    return;
  }

  switch (event.type) {
    case 'playing':
      trackPlayEvent(event, videoLoop);
      break;
    case 'pause':
      trackPauseEvent(event);
      break;
    case 'ended':
      trackEndedEvent(event, videoLoop);
      break;
    case 'timeupdate': {
      const videoPercentage = event.base.videoPercentageViewed;
      const declaredVideoPercentage = declareTrackingVideoPercentage(videoPercentage);
      if (declaredVideoPercentage) {
        const hasPercentageTracked = checkIfPercentageIsTracked(declaredVideoPercentage, trackedPercentages);
        if (!hasPercentageTracked) {
          trackTimeUpdateEvent(event, declaredVideoPercentage, videoLoop);
        }
      }
      break;
    }
    case 'error': {
      trackVideoError(event, event.errorCode);
      break;
    }
  }
};

const checkIfPercentageIsTracked = (key: number | null, trackedPercentages: RefObject<TrackedPercentage>) => {
  if (trackedPercentages.current[key]) {
    return true;
  }

  trackedPercentages.current[key] = true;
  return false;
};

const getDefaultTrackingData = (event: RocheUIVideoPlayerEvent) => {
  return {
    event: 'video_tracking',
    component_name: 'roche-video-player',
    additionalParameters: {
      videoId: event.base.videoId,
      video_title: event.base.videoTitle,
      video_percentage: event.base.videoPercentageViewed,
    },
  };
};

const getDefaultAdobeTrackingData = (event: RocheUIVideoPlayerEvent) => {
  return {
    name: event.base.videoTitle,
    url: event.base.videoUrl,
    provider: event.base.videoType,
    durationTotal: event.base.videoDurationTotal,
  };
};

export const trackPlayEvent = (event: RocheUIVideoPlayerEvent, isVideoLoop: boolean) => {
  const defaultTrackingData = getDefaultTrackingData(event);
  const defaultAdobeTrackingData = getDefaultAdobeTrackingData(event);

  TrackingService.push({
    ...defaultTrackingData,
    value: event.trackingValue,
  });

  if (!isVideoLoop) {
    AdobeTracking(
      {
        ...defaultAdobeTrackingData,
        autoplay: isVideoLoop ? isVideoLoop : false,
      },
      'videoStart',
    );
  }
};

export const trackPauseEvent = (event: RocheUIVideoPlayerEvent) => {
  const defaultTrackingData = getDefaultTrackingData(event);

  TrackingService.push({
    ...defaultTrackingData,
    value: event.trackingValue,
  });
};

export const trackVideoError = (event: RocheUIVideoPlayerEvent, errorCode: string | number) => {
  const defaultAdobeTrackingData = getDefaultAdobeTrackingData(event);

  // YouTube returns errorCode numbers... parse it.
  const errorCodeString: string = typeof errorCode === 'number' ? String(errorCode) : errorCode;
  AdobeTracking(
    {
      ...defaultAdobeTrackingData,
      error: errorCodeString,
    },
    'videoError',
  );
};

export const trackEndedEvent = (event: RocheUIVideoPlayerEvent, isVideoLoop: boolean) => {
  const defaultTrackingData = getDefaultTrackingData(event);
  const defaultAdobeTrackingData = getDefaultAdobeTrackingData(event);

  // coming from component-lib, "ended" default event only being tracked for brightcove:
  if (event.base.videoType === 'brightcove') {
    TrackingService.push({
      ...defaultTrackingData,
      value: 'end',
    });
  }

  if (!isVideoLoop) {
    AdobeTracking(
      {
        ...defaultAdobeTrackingData,
        progress: 100,
      },
      'videoComplete',
    );
  }
};

export const trackTimeUpdateEvent = (event: RocheUIVideoPlayerEvent, videoPercentage: number, isVideoLoop: boolean) => {
  const defaultTrackingData = getDefaultTrackingData(event);
  const defaultAdobeTrackingData = getDefaultAdobeTrackingData(event);

  if (videoPercentage >= Percentage.TWENTY_FIVE) {
    TrackingService.push({
      ...defaultTrackingData,
      value: 'video_progress',
    });
  }

  if (videoPercentage <= Percentage.SEVENTY_FIVE && !isVideoLoop) {
    AdobeTracking(
      {
        ...defaultAdobeTrackingData,
        progress: videoPercentage,
      },
      'videoProgress',
    );
  }
};

export enum Percentage {
  ZERO = 0,
  TEN = 10,
  TWENTY_FIVE = 25,
  FIFTY = 50,
  SEVENTY_FIVE = 75,
  HUNDRED = 100,
}

export const declareTrackingVideoPercentage = (videoPercentage: number): Percentage | undefined => {
  if (videoPercentage < Percentage.TEN) {
    return undefined;
  }
  if (videoPercentage < Percentage.TWENTY_FIVE) {
    return Percentage.TEN;
  }
  if (videoPercentage < Percentage.FIFTY) {
    return Percentage.TWENTY_FIVE;
  }
  if (videoPercentage < Percentage.SEVENTY_FIVE) {
    return Percentage.FIFTY;
  }
  if (videoPercentage < Percentage.HUNDRED) {
    return Percentage.SEVENTY_FIVE;
  }
  return Percentage.HUNDRED;
};
